import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from '@mui/material'
import { formatDate, formatCurrency } from 'src/utils/Formatter'
import { GetPaymentDetails } from 'src/services/WorkflowService'
import { InvoicePaymentSummary } from 'src/viewModels/workflow/InvoicePaymentSummary'
import LocalAtmIcon from '@mui/icons-material/LocalAtm'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'

interface IPastReceivedPaymentsModalProps {
  open: boolean
  caseLineItemId?: number | null
  onClose: () => void
}

const PastReceivedPaymentsModal: React.FC<IPastReceivedPaymentsModalProps> = ({
  open,
  caseLineItemId,
  onClose,
}) => {
  const [paymentDetails, setPaymentDetails] = useState<InvoicePaymentSummary | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && caseLineItemId) {
      setLoading(true)
      GetPaymentDetails(Number(caseLineItemId))
        .then((details) => setPaymentDetails(details || null))
        .catch(() => setPaymentDetails(null))
        .finally(() => setLoading(false))
    } else {
      setPaymentDetails(null)
    }
  }, [open, caseLineItemId])

  const appliedPayments = paymentDetails?.payments || []

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <ResponsiveAppBar
        title={`Received Checks - ${
          caseLineItemId ? `Group #: ${paymentDetails?.groupNumber}` : ''
        }`}
        pageIcon={<LocalAtmIcon />}
        isModal={true}
      />
      <DialogContent>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <CircularProgress size={32} />
          </div>
        ) : (
          <Table size="medium" style={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell align="right">Check Date</TableCell>
                <TableCell>Check Number</TableCell>
                <TableCell>Payer</TableCell>
                <TableCell align="right">Applied Date</TableCell>
                <TableCell>Applied By</TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }} align="right">
                  Amount Paid
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appliedPayments.length > 0 ? (
                appliedPayments.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell align="right">
                      {formatDate(record.checkDate?.toString())}
                    </TableCell>
                    <TableCell>{record.checkNumber}</TableCell>
                    <TableCell>{record.payer}</TableCell>
                    <TableCell align="right">
                      {formatDate(record.appliedDatePaid?.toString())}
                    </TableCell>
                    <TableCell>{record.appliedBy}</TableCell>
                    <TableCell align="right">
                      {record.amountPaid
                        ? formatCurrency(String(record.amountPaid))
                        : undefined}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow key="blank">
                  <TableCell colSpan={6} align="center">
                    <i>No records found</i>
                  </TableCell>
                </TableRow>
              )}
              <TableRow key="footer">
                <TableCell colSpan={4}>
                  <div />
                </TableCell>
                <TableCell align="right">
                  <strong>Total:</strong>
                </TableCell>
                <TableCell style={{ width: '8%' }} align="right">
                  {appliedPayments.length > 0 ? (
                    <span>
                      {formatCurrency(
                        appliedPayments
                          .map((x) => Number(x.amountPaid) || 0)
                          .reduce((a, b) => a + b, 0)
                          .toString()
                      )}
                    </span>
                  ) : (
                    <span>[UNKNOWN]</span>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PastReceivedPaymentsModal
